import type {
  ProductPageKey,
  ProductPagePayload,
} from "../../shared/contracts/product-pages.js";
import { isProductPageAccessDenied } from "../api/product-page-error.js";

export interface ProductPageScope {
  readonly accountId: string | null;
}
type Loader = (page: ProductPageKey) => Promise<ProductPagePayload>;

/** One signed-in account, one generation. Retired requests cannot refill it. */
export class ProductPageCache {
  private scope: ProductPageScope = { accountId: null };
  private readonly pages = new Map<ProductPageKey, ProductPagePayload>();
  private readonly pending = new Map<ProductPageKey, symbol>();

  public selectAccount(accountId: string | null): ProductPageScope {
    if (accountId !== this.scope.accountId) this.clear(accountId);
    return this.scope;
  }

  public clear(accountId = this.scope.accountId): void {
    this.pages.clear();
    this.pending.clear();
    this.scope = { accountId };
  }

  public isCurrent(scope: ProductPageScope): boolean {
    return scope === this.scope && scope.accountId !== null;
  }

  public get(
    scope: ProductPageScope,
    page: ProductPageKey,
  ): ProductPagePayload | null {
    return this.isCurrent(scope) ? (this.pages.get(page) ?? null) : null;
  }

  public async load(
    scope: ProductPageScope,
    page: ProductPageKey,
    loader: Loader,
  ): Promise<ProductPagePayload | null> {
    if (!this.isCurrent(scope)) return null;
    const request = Symbol();
    this.pending.set(page, request);
    const current = () =>
      this.isCurrent(scope) && this.pending.get(page) === request;
    try {
      const payload = await loader(page);
      if (!current()) return null;
      this.pages.set(page, payload);
      return payload;
    } catch (error) {
      if (!current()) return null;
      if (isProductPageAccessDenied(error)) this.pages.delete(page);
      throw error;
    } finally {
      if (current()) this.pending.delete(page);
    }
  }

  /** Capture ownership before scheduling, never when the idle callback runs. */
  public prefetch(
    accountId: string,
    pages: ProductPageKey[],
    loader: Loader,
  ): () => void {
    const scope = this.scope;
    return () => {
      if (scope.accountId !== accountId || !this.isCurrent(scope)) return;
      for (const page of pages) {
        if (this.get(scope, page) || this.pending.has(page)) continue;
        void this.load(scope, page, loader).catch(() => undefined);
      }
    };
  }
}
